/**
 * dst-scroll-hero — canvas-based image-sequence scroll scrubber.
 *
 * Usage: <dst-scroll-hero data-frame-count="90" data-base-url="https://..." ...>
 *
 * On mobile (≤749 px) the element bails out immediately and CSS keeps the
 * fallback image visible — no canvas, no scroll listener, no preloading.
 *
 * Multiple "reels"
 *   The canvas plays through one or more reels back to back: reel 0 is the
 *   main hero sequence (data-base-url etc. on the element itself); reels 1..N
 *   are chapters (`.dst-sh__chapter` elements, one per Chapter block), each
 *   optionally carrying its own data-frame-base-url/-count/-ext/-pad. A
 *   chapter with no base URL of its own has no reel of its own — the canvas
 *   just keeps showing whatever the previous reel last painted. Seamlessness
 *   between reels (matching first/last frames) is a content concern, not
 *   something this script tries to enforce.
 *
 * Preloading strategy
 *   1. Reel 0's first 30 frames load synchronously (blocking until done) so
 *      frame 0 is painted before the scroll listener fires.
 *   2. Reel 0's remaining frames load sequentially via requestIdleCallback
 *      (fallback: setTimeout), then the next reel with a sequence of its own
 *      starts loading the same way, and so on down the chain.
 *
 * If the user scrolls to a frame that hasn't loaded yet the nearest already-
 * loaded frame (within that reel) is shown instead and a loading indicator
 * appears.
 */

class DstScrollHero extends HTMLElement {
  connectedCallback() {
    // ── Mobile bail-out — CSS handles the fallback image ──────────────────────
    if (window.matchMedia('(max-width: 749px)').matches) return;

    // ── Guard: section not configured yet ─────────────────────────────────────
    if (!this.dataset.baseUrl) return;

    this._canvas     = this.querySelector('.dst-sh__canvas:not(.dst-sh__canvas--fade)');
    this._ctx        = this._canvas.getContext('2d');
    this._fadeCanvas = this.querySelector('.dst-sh__canvas--fade');
    this._fadeCtx    = this._fadeCanvas.getContext('2d');
    this._indicator  = this.querySelector('[data-loading]');
    this._progressEl = this.querySelector('.dst-sh__progress');

    this._fit            = this.dataset.fit || 'contain';
    this._resizeTimer    = null;
    this._currentReel    = -1;
    this._currentFrame   = -1;
    this._activeChapter  = -1;
    this._lastPaintedImg = null;

    // Reel 0 = the main hero sequence.
    this._reels = [
      this._makeReel({
        baseUrl:    this.dataset.baseUrl,
        ext:        this.dataset.ext || 'webp',
        pad:        parseInt(this.dataset.pad, 10) || 3,
        frameCount: parseInt(this.dataset.frameCount, 10) || 90,
        vh:         parseFloat(this.dataset.scrollVh) || 300,
      }),
    ];

    // Reels 1..N = chapters, one per .dst-sh__chapter (in DOM order). The
    // first chapter ships with `is-active` in the server-rendered HTML as a
    // no-JS fallback (see the <noscript> override below) — strip it here so
    // JS-enabled visitors never see chapter 1 before they've actually
    // scrolled into the chapter phase.
    this._chapterEls = Array.from(this.querySelectorAll('.dst-sh__chapter'));
    this._chapterEls.forEach(el => el.classList.remove('is-active'));
    this._overlayEl = this.querySelector('.dst-sh__overlay');
    this._chapterEls.forEach(el => {
      const vh = parseFloat(el.dataset.scrollVh) || 150;
      const baseUrl = el.dataset.frameBaseUrl || '';
      const crossfadeVh = parseFloat(el.dataset.crossfadeVh) || 0;
      if (baseUrl) {
        this._reels.push(this._makeReel({
          baseUrl,
          ext:        el.dataset.frameExt || 'webp',
          pad:        parseInt(el.dataset.framePad, 10) || 3,
          frameCount: parseInt(el.dataset.frameCount, 10) || 1,
          vh,
          crossfadeVh,
        }));
      } else {
        // No sequence of its own — just a hold. Nothing to preload/draw, and
        // nothing to crossfade into.
        this._reels.push({ baseUrl: '', frameCount: 0, vh, crossfadeVh: 0, frames: [], loaded: new Set() });
      }
    });

    // For each reel, the nearest earlier reel that actually has its own
    // frame sequence — what a crossfade-in should blend from. Computed once
    // up front rather than inferred from "whatever the canvas last painted,"
    // which broke on reverse-scroll: scrubbing forward through a reel and
    // then back up to its own start repaints the canvas with that reel's
    // *own* frames first, so by the time you're back in its crossfade zone
    // there's no record left of the previous reel to blend from.
    this._predecessorReel = this._reels.map((reel, i) => {
      for (let j = i - 1; j >= 0; j--) {
        if (this._reels[j].baseUrl && this._reels[j].frameCount > 0) return this._reels[j];
      }
      return null;
    });
    this._crossfadeBaseReel = null;

    // Reveal canvas, hide fallback via CSS class
    this.classList.add('dst-sh--js-ready');
    this._resizeCanvas();

    const hero = this._reels[0];
    const initialBatch = Math.min(30, hero.frameCount);
    this._preloadRange(hero, 0, initialBatch, () => {
      this._currentReel  = 0;
      this._currentFrame = 0;
      this._drawReelFrame(0, 0);
      this._scrollGate = new DstScrollUtils.ScrollGate(this, () => this._onScrollRAF());
      this._startBackgroundLoad(hero, initialBatch);
    });

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._resizeCanvas(), 150);
    }, { passive: true });
  }

  disconnectedCallback() {
    if (this._scrollGate) this._scrollGate.destroy();
  }

  // ── Reels ────────────────────────────────────────────────────────────────────

  _makeReel({ baseUrl, ext, pad, frameCount, vh, crossfadeVh }) {
    return { baseUrl, ext, pad, frameCount, vh, crossfadeVh: crossfadeVh || 0, frames: new Array(frameCount), loaded: new Set() };
  }

  // ── URL ──────────────────────────────────────────────────────────────────────

  _frameUrl(reel, index) {
    // Frames are 1-based on disk: 001.webp, 002.webp …
    const n = String(index + 1).padStart(reel.pad, '0');
    return `${reel.baseUrl}${n}.${reel.ext}`;
  }

  // ── Loading ───────────────────────────────────────────────────────────────────

  _loadFrame(reel, index) {
    return new Promise(resolve => {
      if (reel.loaded.has(index)) { resolve(); return; }
      const img = new Image();
      img.onload  = () => { reel.loaded.add(index); resolve(); };
      img.onerror = () => { resolve(); }; // skip silently — drawNearest handles gaps
      img.src = this._frameUrl(reel, index);
      reel.frames[index] = img;
    });
  }

  _preloadRange(reel, from, to, callback) {
    let remaining = to - from;
    if (remaining <= 0) { callback(); return; }
    for (let i = from; i < to; i++) {
      this._loadFrame(reel, i).then(() => { if (--remaining === 0) callback(); });
    }
  }

  // Loads every remaining frame across every reel at once, with several
  // requests in flight concurrently. Chaining reels strictly one-at-a-time
  // (finish reel 0 entirely before reel 1 starts) couldn't keep up once a
  // page had several chapter reels on top of the hero (5+ reels × 75
  // frames) — a visitor scrolling at a normal pace would reach a later
  // chapter before its frames had even started loading.
  //
  // Priority order: every content reel's frame 0 AND last frame first (frame
  // 0 is what that reel's own crossfade-in needs; the last frame is what
  // the *next* reel's crossfade needs as its blend-from base — missing
  // either causes a visible flicker/stall or, worse, a stale frame from an
  // unrelated earlier chapter left on screen), then each reel's remaining
  // frames — needed once scrubbing actually starts, which is later than
  // either crossfade — in page order.
  _startBackgroundLoad(hero, doneCount) {
    const contentReels = [hero, ...this._reels.slice(1).filter(r => r.baseUrl && r.frameCount > 0)];

    const queue = [];
    const queued = new Map(); // reel -> Set(indices already queued)
    const queueFrame = (reel, index) => {
      if (reel.loaded.has(index)) return;
      let set = queued.get(reel);
      if (!set) { set = new Set(); queued.set(reel, set); }
      if (set.has(index)) return;
      set.add(index);
      queue.push({ reel, index });
    };

    for (const reel of contentReels) {
      queueFrame(reel, 0);
      queueFrame(reel, reel.frameCount - 1);
    }
    for (const reel of contentReels) {
      const start = reel === hero ? doneCount : 0;
      for (let i = start; i < reel.frameCount; i++) queueFrame(reel, i);
    }

    const CONCURRENCY = 6;
    let cursor = 0;
    const pullNext = () => {
      if (cursor >= queue.length) return;
      const { reel, index } = queue[cursor++];
      this._loadFrame(reel, index).then(pullNext);
    };
    for (let i = 0; i < CONCURRENCY; i++) pullNext();
  }

  // ── Canvas ────────────────────────────────────────────────────────────────────

  _resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    // Use clientWidth/Height of the canvas element (set to 100% via CSS)
    this._cw = this._canvas.clientWidth;
    this._ch = this._canvas.clientHeight;
    this._canvas.width  = this._cw * dpr;
    this._canvas.height = this._ch * dpr;
    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this._fadeCanvas.width  = this._cw * dpr;
    this._fadeCanvas.height = this._ch * dpr;
    this._fadeCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Repaint after resize
    this._drawReelFrame(this._currentReel, this._currentFrame);
  }

  _drawReelFrame(reelIndex, frameIndex) {
    const reel = this._reels[reelIndex];
    if (!reel || !reel.baseUrl || reel.frameCount === 0) {
      // No sequence for this reel — keep showing whatever was last painted
      // (matters on resize: without this the canvas would just go blank).
      if (this._lastPaintedImg) this._paint(this._lastPaintedImg);
      this._setLoading(false);
      return;
    }
    const frame = reel.frames[frameIndex];
    if (!frame || !reel.loaded.has(frameIndex)) {
      this._drawNearest(reel, frameIndex);
      this._setLoading(true);
      return;
    }
    this._setLoading(false);
    this._paint(frame);
  }

  // Walk outward from target to find the closest already-loaded frame within
  // this reel (never crosses into another reel — a stand-in frame should
  // still be the right story, just the wrong rotation angle).
  _nearestLoadedFrame(reel, target) {
    if (reel.loaded.has(target)) return reel.frames[target];
    for (let d = 1; d < reel.frameCount; d++) {
      const lo = target - d;
      const hi = target + d;
      if (lo >= 0 && reel.loaded.has(lo)) return reel.frames[lo];
      if (hi < reel.frameCount && reel.loaded.has(hi)) return reel.frames[hi];
    }
    return null;
  }

  _drawNearest(reel, target) {
    const frame = this._nearestLoadedFrame(reel, target);
    if (frame) this._paint(frame);
  }

  // Shared "where does this image land in the canvas box" math, used both for
  // the real paint and for sizing the fade-reveal canvas identically.
  _computeDraw(img) {
    const cw = this._cw;
    const ch = this._ch;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const scale = this._fit === 'cover'
      ? Math.max(cw / iw, ch / ih)
      : Math.min(cw / iw, ch / ih); // contain (default)
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;
    return { dx, dy, dw, dh };
  }

  _paint(img) {
    this._lastPaintedImg = img;

    const { dx, dy, dw, dh } = this._computeDraw(img);
    this._ctx.clearRect(0, 0, this._cw, this._ch);
    this._ctx.drawImage(img, dx, dy, dw, dh);

    // Report where the image actually sits within the canvas box, so text
    // overlays (chapter captions, main hero overlay) can position themselves
    // relative to the *visible image*, not the raw viewport-height box —
    // with `contain` fit on an aspect ratio the box doesn't match (e.g. a
    // tall/narrow window), the letterboxed gaps otherwise leave "bottom"-
    // anchored text sitting well below the actual picture. `cover` has no
    // letterbox gap (image fills edge-to-edge, cropped) so bounds == the
    // full canvas there.
    if (this._fit === 'cover') {
      this._setImageBounds(0, 0, this._cw, this._ch);
    } else {
      this._setImageBounds(dx, dy, dw, dh);
    }
  }

  _setImageBounds(x, y, w, h) {
    this.style.setProperty('--dst-sh-img-top', `${y}px`);
    this.style.setProperty('--dst-sh-img-left', `${x}px`);
    this.style.setProperty('--dst-sh-img-width', `${w}px`);
    this.style.setProperty('--dst-sh-img-height', `${h}px`);
  }

  // Makes sure the primary canvas is actually showing the predecessor
  // reel's last frame before a crossfade blends on top of it — explicit,
  // rather than trusting whatever the canvas happens to already hold,
  // which is wrong on reverse scroll (see _predecessorReel comment above).
  // No-ops once already correct, so this is cheap to call every tick.
  _ensureCrossfadeBase(predecessor) {
    if (!predecessor || this._crossfadeBaseReel === predecessor) return;
    const lastIndex = predecessor.frameCount - 1;
    if (predecessor.loaded.has(lastIndex)) {
      this._paint(predecessor.frames[lastIndex]);
      this._crossfadeBaseReel = predecessor;
      return;
    }
    // Exact last frame isn't loaded yet — paint the nearest one that is
    // (almost certainly frame 0, since that's preloaded first) as an
    // interim stand-in, so the base is at least the *right story*, not
    // whatever an earlier, unrelated chapter last painted. Deliberately
    // doesn't set _crossfadeBaseReel here, so this keeps re-checking every
    // tick and upgrades to the real last frame the moment it finishes
    // loading, rather than settling for the stand-in permanently.
    this._loadFrame(predecessor, lastIndex);
    const standIn = this._nearestLoadedFrame(predecessor, lastIndex);
    if (standIn) this._paint(standIn);
  }

  // Paints `frame` onto the fade-reveal canvas at a caller-supplied opacity —
  // driven directly by scroll position every tick (see _onScrollRAF), so
  // scrubbing back and forth scrubs the blend itself, not a fixed-duration
  // animation. The primary canvas keeps showing whatever it last painted
  // (the outgoing reel's last frame) underneath throughout.
  _showFadeReveal(frame, alpha) {
    if (!frame) { this._setLoading(true); return; }
    this._setLoading(false);
    const { dx, dy, dw, dh } = this._computeDraw(frame);
    this._fadeCtx.clearRect(0, 0, this._cw, this._ch);
    this._fadeCtx.drawImage(frame, dx, dy, dw, dh);
    this._fadeCanvas.style.opacity = alpha;
  }

  _hideFadeReveal() {
    this._fadeCanvas.style.opacity = 0;
  }

  _setLoading(visible) {
    if (this._indicator) this._indicator.hidden = !visible;
  }

  // ── Scroll ────────────────────────────────────────────────────────────────────

  _onScrollRAF() {
    // scrollable = height of the section minus one viewport (the distance the
    // section travels while the sticky canvas is pinned at the top). Covers
    // every reel's track, back to back.
    const scrollable = this.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const scrolledPx = Math.max(0, Math.min(scrollable, -this.getBoundingClientRect().top));

    if (this._progressEl) {
      this._progressEl.style.width = `${(scrolledPx / scrollable) * 100}%`;
    }

    const totalVh = this._reels.reduce((sum, r) => sum + r.vh, 0) || 1;

    let reelIndex = 0;
    let localPx = 0;
    let segPx = 0;
    let acc = 0;
    for (let i = 0; i < this._reels.length; i++) {
      const seg = scrollable * (this._reels[i].vh / totalVh);
      if (scrolledPx <= acc + seg || i === this._reels.length - 1) {
        reelIndex = i;
        segPx = seg;
        localPx = Math.max(0, Math.min(seg, scrolledPx - acc));
        break;
      }
      acc += seg;
    }

    const reel = this._reels[reelIndex];
    // The reel's own vh budget carries its crossfade zone at the start —
    // frame 0 holds while it dissolves in over the previous reel's last
    // frame, scrubbed directly by scroll position (not a timer). The
    // remaining budget scrubs the reel's frames as before.
    const crossfadeVh = Math.min(reel.crossfadeVh || 0, reel.vh);
    const crossfadePx = segPx > 0 ? segPx * (crossfadeVh / reel.vh) : 0;

    if (reel.baseUrl && reel.frameCount > 0) {
      if (crossfadePx > 0 && localPx < crossfadePx) {
        const alpha = localPx / crossfadePx;
        this._currentReel  = reelIndex;
        this._currentFrame = 0;
        this._ensureCrossfadeBase(this._predecessorReel[reelIndex]);
        if (reel.loaded.has(0)) {
          this._showFadeReveal(reel.frames[0], alpha);
        } else {
          // Frame not loaded yet — an Image() object may already exist
          // mid-flight (reel.frames[0] set, not yet decoded), but drawing
          // it has zero natural dimensions and leaves the fade canvas
          // stuck on stale content from whatever it last painted. Kick off
          // the load and just wait rather than draw garbage.
          this._loadFrame(reel, 0);
          this._setLoading(true);
          this._hideFadeReveal();
        }
      } else {
        const scrubPx = segPx - crossfadePx;
        const localScrub = scrubPx > 0 ? Math.max(0, Math.min(1, (localPx - crossfadePx) / scrubPx)) : 1;
        const frameIndex = Math.round(localScrub * (reel.frameCount - 1));
        if (reelIndex !== this._currentReel || frameIndex !== this._currentFrame) {
          this._currentReel  = reelIndex;
          this._currentFrame = frameIndex;
          this._drawReelFrame(reelIndex, frameIndex);
        }
        this._hideFadeReveal();
        this._crossfadeBaseReel = null;
      }
    } else {
      this._currentReel = reelIndex;
      this._hideFadeReveal();
      this._crossfadeBaseReel = null;
    }

    // Reel 0 is the hero (no chapter active); reel N corresponds to chapter N-1.
    this._setActiveChapter(reelIndex - 1);

    // Clean handoff: fade the main hero caption out once we're into any
    // chapter, so it doesn't sit on screen behind/alongside chapter text.
    if (this._overlayEl) {
      this._overlayEl.classList.toggle('dst-sh__overlay--hidden', reelIndex > 0);
    }
  }

  _setActiveChapter(index) {
    if (index === this._activeChapter || this._chapterEls.length === 0) return;
    this._activeChapter = index;
    this._chapterEls.forEach((el, i) => el.classList.toggle('is-active', i === index));
  }
}

customElements.define('dst-scroll-hero', DstScrollHero);
